import { EnrollmentPlanCSIDWithUXControls } from "@/api/types";
import { useEnrollmentContext } from "@/state";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

export const useSelectedEnrollmentPlan = () => {
  const experienceData = useEnrollmentContext();
  const [enrollmentPlanCsid, setEnrollmentPlanCsid] = useState<EnrollmentPlanCSIDWithUXControls>(undefined);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const planFromParams = searchParams.get("enrollmentPlan");
    if (planFromParams) {
      const plans = experienceData.plan_csids;
      const selectedPlan = plans.find((ep) => ep.id === +planFromParams);
      setEnrollmentPlanCsid(selectedPlan);
    }
  }, [searchParams, experienceData]);

  /**
   * Fetch a UX control value.
   *
   * @param controlName key for the UX control to return
   *
   * @return control's value
   */
  const getControlValue = useCallback((controlName: string) => {
    // check plan controls
    const uxControls = enrollmentPlanCsid?.ux_controls || [];
    let controlValue = uxControls.find((c) => c.control === controlName)?.value;

    // if no plan customization value found, check site-level or global default controls instead
    if (!controlValue) {
      controlValue = experienceData.ux_controls.find((c) => c.control === controlName)?.value;
    }

    return controlValue;
  }, [enrollmentPlanCsid, experienceData]);

  const customerServicePhoneNumber = getControlValue("customer_service_phone_number");

  const enrollmentSupportPhoneNumber = getControlValue("customer_service_phone_number_enrollment_support");

  const memberSupportPhoneNumber = getControlValue("customer_service_phone_number_member_support");

  const hideDrugPricing = getControlValue("hide_drug_pricing") == "true";

  const hideMailPharmacyOnFBPTool = getControlValue("hide_mail_pharmacy_on_fbp_tool") == "true";

  const hideMedicarePrescriptionPaymentPlanPage = getControlValue("hide_medicare_prescription_payment_plan") == "true";

  const hidePharmacySearch = getControlValue("hide_pharmacy_search") == "true";

  const hidePlanDocuments = getControlValue("hide_plan_documents") == "true";

  const hideFormularyLookup = getControlValue("hide_formulary_lookup") == "true";

  const hideOnlinePharmacyOnFBPTool = getControlValue("hide_online_pharmacy_on_fbp_tool") == "true";

  const hidePlanCompare = getControlValue("hide_plan_compare") == "true";

  const mailPharmacyIdentifier = getControlValue("mail_pharmacy_identifier");

  const medicarePrescriptionPaymentPlanPhoneSection = getControlValue("medicare_prescription_payment_plan_phone_section");

  const medicarePrescriptionPaymentPlanOnlineSection = getControlValue("medicare_prescription_payment_plan_online_section");

  const medicarePrescriptionPaymentPlanMailSection = getControlValue("medicare_prescription_payment_plan_mail_section");

  const medicarePrescriptionPaymentPlanEmailSection = getControlValue("medicare_prescription_payment_plan_email_section");

  const medicarePrescriptionPaymentPlanExplanationSection = getControlValue("medicare_prescription_payment_plan_explanation_section");

  const onlinePharmacyIdentifier = getControlValue("online_pharmacy_identifier");

  const overrideFormularyLookupURL = getControlValue("override_formulary_lookup_url");

  const planPageDescription = getControlValue("plans_page_description");

  return {
    enrollmentPlanCsid,
    enrollmentSupportPhoneNumber,
    customerServicePhoneNumber,
    hideDrugPricing,
    hideMailPharmacyOnFBPTool,
    hideMedicarePrescriptionPaymentPlanPage,
    hideOnlinePharmacyOnFBPTool,
    hidePharmacySearch,
    hidePlanDocuments,
    hideFormularyLookup,
    hidePlanCompare,
    mailPharmacyIdentifier,
    memberSupportPhoneNumber,
    medicarePrescriptionPaymentPlanPhoneSection,
    medicarePrescriptionPaymentPlanOnlineSection,
    medicarePrescriptionPaymentPlanEmailSection,
    medicarePrescriptionPaymentPlanMailSection,
    medicarePrescriptionPaymentPlanExplanationSection,
    onlinePharmacyIdentifier,
    overrideFormularyLookupURL,
    planPageDescription,
  };
};
